import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { from, Observable, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { BillReaderAuthService } from 'src/app/bill-reader/services/bill-reader-auth.service';
import { SecurityService } from 'src/app/security/security.service';
import { environment } from '../../../environments/environment';
import { AuthFields, HttpError } from '../models';

@Injectable()
export class HttpRequestInterceptor implements HttpInterceptor {
	constructor(
		private securityService: SecurityService,
		private billReaderAuthService: BillReaderAuthService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const operationName = req.body && req.body.operationName ? req.body.operationName : '';
		const isAuth = operationName.includes('AuthenticateUser');
		const isRefresh = operationName.includes('RefreshToken');

		return from(this.securityService.getAuthFields(isRefresh)) // make sure we're always getting the latest token
			.pipe(
				switchMap(authFields => {
					const token = this.getToken(authFields, isRefresh);
					let headers = req.headers.set('Authorization', 'Bearer ' + token);

					// get the bill-reader token
					const billReaderKey = this.billReaderAuthService.getBillReaderToken();
					if (billReaderKey && this.isGraphqlEndpoint(req)) {
						headers = headers.set('X-Bill-Reader-Key', billReaderKey);
					}

					const clonedRequest = req.clone({ headers });

					return next.handle(clonedRequest).pipe(
						map((result: HttpEvent<any>) => {
							if (result instanceof HttpResponse && result.body?.errors) {
								throw result;
							}
							return result;
						}),
						catchError((error: any) => {
							if (!isRefresh && !isAuth && error.body?.errors?.length) {
								const errors = error.body.errors.map(e => e.extensions?.code?.toLowerCase()).join(',');
								if (!authFields.isTokenExpired() && errors.includes('unauthenticated')) {
									// this situation only really happens when the token is valid but it's in the wrong environment
									// due to the developer doing testing between environments
									this.securityService.logout();
								}
							}
							if (error.body?.errors?.length) {
								return throwError(
									() =>
										new HttpError(
											error.body.errors.map(e => e.message).join(','),
											error.body.errors.map(e => e.extensions?.displayMessage ?? '').join(','),
										),
								);
							}
							return throwError(() => error);
						}),
					);
				}),
			);
	}

	private getToken(authFields: AuthFields, isRefresh: boolean): string {
		if (this.securityService.isDirect || !authFields?.token) {
			return environment.apiKey;
		}
		return isRefresh ? authFields.refreshToken : authFields.token;
	}

	private isGraphqlEndpoint(req: HttpRequest<any>): boolean {
		const url = req.url;
		const endpoint = environment.graphQLEndpoint;
		return url === endpoint || (!!endpoint && url.endsWith('/graphql'));
	}
}
